'use client'
import { EditableText } from '@/components/content-edit/editable-text'
import { Icon } from '@/components/ui/icon'
import { Bone } from '@/components/ui/bone'
import { useCurrency } from '@/hooks/use-currency'
import { ProductItem } from '@/lib/types/operations/product'
import { IProductVariantExtended } from '@/lib/types/operations/product-variant'
import type { IMediaItem } from '@/lib/types/media'
import { getUnitDisplay } from '@/lib/types/core/unit-of-measure'
import { resolvePrice, type PriceTier } from '@/lib/utils/price-resolution'
import { Suspense, useMemo } from 'react'
import { getProductDocsComponent } from './product-docs'
import { ProductMediaGallery } from './product-media-gallery'
import { ProductStickyCTA } from './product-sticky-cta'
import { UnifiedProductActions } from './unified-product-actions'
import { useVariantSelection, variantMediaItems, type VariantInventoryStatus } from './variant-selector'

interface ProductStats {
  hasVariants: boolean;
  inStock: boolean;
  availableQuantity: number;
  averageRating: number;
  totalRatings: number;
  totalReviews: number;
  totalViews: number;
  variantInventory: Array<{
    variantId: string;
    inventory: number;
    inStock: boolean;
  }>;
}

interface ProductContentProps {
  product: ProductItem
  productStats?: ProductStats
  children: React.ReactNode
  isAdmin?: boolean
  categoryName?: string
  priceTier?: PriceTier
}

// useVariantSelection is a hook and must run unconditionally, but
// productStats (the real inventory data) only exists once the stats query
// has resolved. This stands in for it until then; only variantInventory is
// read internally, so an empty list is a safe placeholder.
const EMPTY_VARIANT_INVENTORY: VariantInventoryStatus = { variantInventory: [] }

export function ProductContent({
  product,
  productStats,
  children,
  isAdmin = false,
  categoryName,
  priceTier = 'RETAIL'
}: ProductContentProps) {
  const { format: formatCurrency } = useCurrency()

  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0

  // Per-product "Documentation" section (converted-from-PDF spec sheets,
  // colour posters, etc.) -- see product-docs/index.ts. undefined for every
  // product without an entry, so the section below simply doesn't render.
  const ProductDocs = getProductDocsComponent(product.slug)

  // Owned here (not inside UnifiedProductActions) so the media gallery can
  // read the selected variant and show its photo, not just a grey placeholder.
  const variantSelection = useVariantSelection(
    (product.variants || []) as IProductVariantExtended[],
    productStats ?? EMPTY_VARIANT_INVENTORY,
    priceTier
  )

  // Main preview: selected variant's photo first, then product-level media,
  // then ProductMediaGallery's own placeholder when neither exists.
  const galleryMedia = useMemo((): IMediaItem[] => {
    const variant = variantSelection.selectedVariant
    if (!variant) return product.media
    const items = variantMediaItems(variant)
    if (items.length === 0) return product.media
    return items.map(item => ({
      ...item,
      altText: item.altText || `${product.name} - ${variant.name}`
    }))
  }, [variantSelection.selectedVariant, product.media, product.name])

  const lowestVariantPrice = useMemo(() => {
    if (!product.variants || product.variants.length === 0) return product.price
    const prices = product.variants
      .map(v => resolvePrice(
        {
          retailPrice: v.retailPrice ?? v.pricePerUnit ?? null,
          wholesalePrice: v.wholesalePrice ?? null,
          rollPrice: v.rollPrice ?? null,
          rollLengthUnits: v.rollLengthUnits ?? null
        },
        priceTier
      ))
      .filter((p): p is number => p != null && p > 0)
    return prices.length > 0 ? Math.min(...prices) : product.price
  }, [product.variants, product.price, priceTier])

  const unitOfMeasureDisplay = useMemo(() => {
    const uom = product.unitOfMeasure
    switch (uom) {
      case 'EACH':
      case 'UNIT':
        return 'Sold per unit'
      case 'ROLL':
        return 'Sold by the roll'
      case 'IMPERIAL':
        return 'Sold by the foot'
      case 'SQUAREFOOT':
        return 'Sold per sq ft'
      case 'SHEET':
        return 'Sold per sheet'
      default:
        return `Sold per ${String(uom).toLowerCase()}`
    }
  }, [product.unitOfMeasure])

  const SPEC_CHIP_KEYS = ['materialType', 'finish', 'coverage', 'duration', 'pieceCount'] as const

  const humanizeLabel = (key: string): string => {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()
  }

  const specChips = useMemo(() => {
    if (!product.attributes) return []
    return SPEC_CHIP_KEYS
      .filter(key => product.attributes?.[key])
      .map(key => ({ label: humanizeLabel(key), value: String(product.attributes![key]) }))
  }, [product.attributes])

  const brand = product.attributes?.brand as string | undefined

  const unitLabel = useMemo(() => {
    return getUnitDisplay(product.unitOfMeasure)
  }, [product.unitOfMeasure])

  const featuresBullets = useMemo(() => {
    if (!product.description) return { intro: '', bullets: [] }
    const sentences = product.description.split('. ').map(s => s.trim()).filter(Boolean)
    if (sentences.length <= 2) {
      return { intro: product.description, bullets: [] }
    }
    const intro = sentences[0] + (sentences[0].endsWith('.') ? '' : '.')
    const bullets = sentences.slice(1).map(s => s.endsWith('.') ? s : s + '.')
    return { intro, bullets }
  }, [product.description])

  const specifications = useMemo(() => {
    const specs: Array<{ label: string; value: string }> = []

    if (product.attributes) {
      Object.entries(product.attributes).forEach(([key, value]) => {
        if (value && String(value).trim()) {
          specs.push({ label: humanizeLabel(key), value: String(value) })
        }
      })
    }

    if (product.width) specs.push({ label: 'Width', value: `${product.width}"` })
    if (product.height) specs.push({ label: 'Height', value: `${product.height}"` })
    if (product.length) specs.push({ label: 'Length', value: `${product.length}"` })

    if (product.unitOfMeasure) {
      specs.push({ label: 'Sold By', value: unitOfMeasureDisplay })
    }

    if (product.sku) specs.push({ label: 'SKU', value: product.sku })

    return specs
  }, [product.attributes, product.width, product.height, product.length, product.unitOfMeasure, product.sku, unitOfMeasureDisplay])

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Product Media Gallery */}
        <div className="relative">
          <Suspense fallback={<div>Loading...</div>}>
            <ProductMediaGallery
              // Remount on variant change: resets the thumbnail index and
              // closes the lightbox instead of pointing them at a now
              // out-of-range or stale image.
              key={variantSelection.selectedVariant?.id ?? product.id ?? product.slug}
              media={galleryMedia}
              productName={product.name}
            />
          </Suspense>
        </div>

        {/* Product Information */}
        <div className="space-y-5">
          {/* Category Badge */}
          <div className='flex flex-wrap gap-2'>
            {categoryName && (
              <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-overlay text-foreground'>
                <Icon icon='tag-horizontal-bold-duotone' className='w-4 h-4' />
                {categoryName}
              </span>
            )}
          </div>

          {/* Product Name + Brand */}
          <div>
            {/* The description is NOT wrapped: this page renders it split into an
                intro plus bullets, so an inline commit would write only the
                fragment back as the whole override. It stays dashboard-edited. */}
            <h1 className="text-3xl font-bold">
              {product.slug ? (
                <EditableText
                  source={{ kind: 'override', entity: 'product', slug: product.slug, field: 'name' }}
                  value={product.name}
                />
              ) : (
                product.name
              )}
            </h1>
            {brand && (
              <p className="text-muted text-sm mt-1">by {brand}</p>
            )}
          </div>

          {/* Key Specs Chips */}
          {specChips.length > 0 && (
            <div className='flex flex-wrap gap-2'>
              {specChips.map(chip => (
                <span key={chip.label} className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-overlay text-foreground'>
                  {chip.value}
                </span>
              ))}
              {product.unitOfMeasure && (
                <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-overlay text-foreground'>
                  <Icon icon='ruler' className='w-4 h-4' />
                  {unitOfMeasureDisplay}
                </span>
              )}
            </div>
          )}

          {/* Price Block */}
          <div className='flex items-baseline gap-3'>
            <span className='text-3xl font-bold'>
              {hasVariants
                ? `From ${formatCurrency(lowestVariantPrice)}`
                : formatCurrency(product.salePrice && product.salePrice > 0 ? product.salePrice : product.price)}
            </span>
            {product.salePrice != null && product.salePrice > 0 && !hasVariants && (
              <span className='text-lg text-muted line-through'>
                {formatCurrency(product.price)}
              </span>
            )}
            <span className='text-sm text-muted'>/ {unitLabel}</span>
          </div>

          {/* Stock Indicator */}
          {productStats && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
              !productStats.inStock
                ? 'bg-danger/15 text-foreground'
                : productStats.availableQuantity > 0 && productStats.availableQuantity <= 10 && !productStats.hasVariants
                  ? 'bg-warning/15 text-foreground'
                  : 'bg-success/15 text-foreground'
            }`}>
              <Icon icon={productStats.inStock ? 'check-circle' : 'close-circle-duotone'} className='w-4 h-4' />
              {!productStats.inStock
                ? 'Out of Stock'
                : productStats.availableQuantity > 0 && productStats.availableQuantity <= 10 && !productStats.hasVariants
                  ? `Low Stock (${productStats.availableQuantity} left)`
                  : 'In Stock'}
            </span>
          )}

          {/* Product Actions */}
          {productStats && (
            <UnifiedProductActions
              product={product}
              productStats={productStats}
              variantSelection={variantSelection}
              className=''
              key={product.id}
              priceTier={priceTier}
            />
          )}

          {children}
        </div>
      </div>

      {/* === Product Details Area === */}
      <div className='mt-12 space-y-10 pb-24 md:pb-0'>
        {/* Features & Benefits */}
        {product.description && (
          <section>
            <h2 className='text-xl font-bold mb-4'>Features & Benefits</h2>
            <p className='text-foreground/80 mb-3'>{featuresBullets.intro}</p>
            {featuresBullets.bullets.length > 0 && (
              <ul className='space-y-2'>
                {featuresBullets.bullets.map((bullet, i) => (
                  <li key={i} className='flex items-start gap-2'>
                    <Icon icon='check-circle' className='w-5 h-5 text-success shrink-0 mt-0.5' />
                    <span className='text-foreground/80'>{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Specifications Table */}
        {specifications.length > 0 && (
          <section>
            <h2 className='text-xl font-bold mb-4'>Specifications</h2>
            <div className='rounded-lg border border-divider overflow-hidden'>
              {specifications.map((spec, i) => (
                <div key={spec.label} className={`flex justify-between px-4 py-2.5 text-sm ${i % 2 === 0 ? 'bg-overlay' : ''}`}>
                  <span className='text-muted font-medium'>{spec.label}</span>
                  <span className='text-foreground'>{spec.value}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Documentation (converted-from-PDF spec sheets, colour posters, etc.) */}
        {ProductDocs && (
          <section>
            <h2 className='text-xl font-bold mb-4'>Documentation</h2>
            <ProductDocs />
          </section>
        )}
      </div>

      {/* Sticky Mobile CTA */}
      {productStats && (
        <ProductStickyCTA product={product} productStats={productStats} priceTier={priceTier} />
      )}
    </>
  )
}

export function ProductContentSkeleton() {
  return (
    <div className="container max-w-screen-xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: media gallery */}
        <div className="relative">
          <Bone className="aspect-4/3 w-full rounded-lg" />
          <div className="grid grid-cols-4 gap-2 mt-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Bone key={i} className="aspect-4/3 rounded-md" />
            ))}
          </div>
        </div>

        {/* Right: product info */}
        <div className="space-y-5">
          {/* Category chip */}
          <Bone className="h-7 w-24 rounded-full" />
          {/* Product name (text-3xl) */}
          <Bone className="h-8 w-3/4" />
          {/* Spec chips */}
          <div className="flex flex-wrap gap-2">
            <Bone className="h-7 w-20 rounded-full" />
            <Bone className="h-7 w-24 rounded-full" />
            <Bone className="h-7 w-28 rounded-full" />
          </div>
          {/* Price block (text-3xl) */}
          <Bone className="h-8 w-40" />
          {/* Stock pill */}
          <Bone className="h-7 w-28 rounded-full" />
          {/* Actions */}
          <div className="space-y-3">
            <Bone className="h-12 w-full" />
            <div className="flex gap-3">
              <Bone className="h-10 w-32" />
              <Bone className="h-10 w-10 rounded-full" />
            </div>
          </div>
        </div>
      </div>

      {/* === Product Details Area === */}
      <div className="mt-12 space-y-10">
        {/* Features & Benefits */}
        <section>
          <Bone className="h-6 w-48 mb-4" />
          <div className="space-y-2 mb-4">
            <Bone className="h-4 w-full" />
            <Bone className="h-4 w-full" />
            <Bone className="h-4 w-2/3" />
          </div>
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-start gap-2">
                <Bone className="h-5 w-5 rounded-full shrink-0" />
                <Bone className="h-4 flex-1" />
              </div>
            ))}
          </div>
        </section>

        {/* Specifications */}
        <section>
          <Bone className="h-6 w-40 mb-4" />
          <div className="rounded-lg border border-border overflow-hidden">
            {Array.from({ length: 5 }).map((_, i) => (
              <Bone key={i} className="h-9 w-full rounded-none" />
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

;(ProductContent as any).Skeleton = ProductContentSkeleton
